import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Image, FileAudio, Video, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`, getAuthHeader());
      setMedia(response.data);
    } catch (error) {
      toast.error("Failed to fetch media");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Determine file type
    let fileType = "image";
    if (file.type.startsWith("audio/")) fileType = "audio";
    else if (file.type.startsWith("video/")) fileType = "video";

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file_type", fileType);

    try {
      await axios.post(`${API}/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("File uploaded successfully");
      fetchMedia();
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await axios.delete(`${API}/media/${id}`, getAuthHeader());
      toast.success("File deleted");
      fetchMedia();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const copyToClipboard = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("URL copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredMedia = activeTab === "all" 
    ? media 
    : media.filter(m => m.file_type === activeTab);

  const getIcon = (type) => {
    switch (type) {
      case "audio": return FileAudio;
      case "video": return Video;
      default: return Image;
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "Unknown";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div data-testid="media-library">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1A2323] mb-2">Media Library</h1>
          <p className="text-[#4A5555]">Upload and manage your images, audio, and video files</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,audio/*,video/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="media-file-input"
          />
          <Button 
            className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            data-testid="upload-media-btn"
          >
            {isUploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload File
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F5F2EB]">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="image" data-testid="tab-images">Images</TabsTrigger>
          <TabsTrigger value="audio" data-testid="tab-audio">Audio</TabsTrigger>
          <TabsTrigger value="video" data-testid="tab-video">Video</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E8B8B]" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <Card className="border-[#E5E0D6]">
              <CardContent className="text-center py-12">
                <Image className="w-12 h-12 text-[#E67E22] mx-auto mb-4 opacity-50" />
                <p className="text-[#4A5555]">No media files yet. Upload your first file!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredMedia.map((item) => {
                const Icon = getIcon(item.file_type);
                return (
                  <Card 
                    key={item.id} 
                    className="border-[#E5E0D6] overflow-hidden group"
                    data-testid={`media-item-${item.id}`}
                  >
                    <div className="aspect-square relative bg-[#F5F2EB]">
                      {item.file_type === "image" ? (
                        <img 
                          src={item.file_url} 
                          alt={item.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon className="w-12 h-12 text-[#4A5555]" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="icon"
                          variant="secondary"
                          className="w-8 h-8"
                          onClick={() => copyToClipboard(item.file_url, item.id)}
                          data-testid={`copy-url-${item.id}`}
                        >
                          {copiedId === item.id ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          className="w-8 h-8"
                          onClick={() => handleDelete(item.id)}
                          data-testid={`delete-media-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm font-medium text-[#1A2323] truncate">{item.filename}</p>
                      <p className="text-xs text-[#4A5555]">{formatSize(item.file_size)}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MediaLibrary;
